/**
 * useViewportHeight Hook
 * Monitors viewport height changes and updates CSS custom property
 * for use in responsive layouts, especially mobile keyboard handling
 *
 * This hook addresses the issue where mobile browsers change viewport height
 * when the virtual keyboard appears, causing layout issues. By setting a CSS
 * custom property --vh, components can use calc(var(--vh, 1vh) * 100) for
 * reliable full-height layouts.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useViewportHeight();
 *   return <div style={{ height: 'calc(var(--vh, 1vh) * 100)' }}>...</div>;
 * }
 * ```
 */

import { useEffect } from 'react';

/**
 * Custom hook that monitors viewport height and updates CSS custom property
 * Handles both window resize events and visualViewport API for better mobile support
 */
export function useViewportHeight(): void {
  useEffect(() => {
    /**
     * Updates the --vh CSS custom property based on current viewport height
     * Uses window.innerHeight / 100 to calculate 1vh equivalent in pixels
     */
    const updateViewportHeight = () => {
      // Calculate 1vh in pixels
      const vh = window.innerHeight * 0.01;

      // Set CSS custom property on document root
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial value on mount
    updateViewportHeight();

    // Listen to window resize events (covers orientation changes)
    window.addEventListener('resize', updateViewportHeight);

    // Listen to visualViewport API if available (better mobile keyboard detection)
    // visualViewport provides more accurate viewport dimensions when keyboard is visible
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
    }

    // Cleanup: Remove all event listeners on unmount
    return () => {
      window.removeEventListener('resize', updateViewportHeight);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount
}

// Default export for flexibility
export default useViewportHeight;
